import {
  ARC_LIVE_DECISION,
  ARC_LIVE_SESSION_UPDATED,
  ARC_LIVE_TRANSCRIPT,
} from '@common/constants/webSockets'
import {
  IArcLiveDecision,
  IArcLiveSegment,
  IArcLiveSession,
  IArcLiveSessionDetail,
  ILiveMeetingsData,
} from '@common/interfaces/arcLive'
import { combineReducers } from 'redux'
import { loadingArrayReducer } from '../../helpers/helpers'
import { createRequestScopedReducer, IRequestScopedState } from '../../helpers/requestScopedReducer'
import { IAction, IActionWithParams } from '../apiMiddleware'
import * as actions from './actions'

export interface IArcLiveState {
  loadingArray: string[]
  liveMeetings: IRequestScopedState<ILiveMeetingsData>
  openSessionId: string | null
  sessionDetail: IRequestScopedState<IArcLiveSessionDetail>
}

/** Puts a started or ended session onto the meeting it belongs to. */
const applySession = (
  data: ILiveMeetingsData | null,
  session: IArcLiveSession,
): ILiveMeetingsData | null =>
  data && {
    meetings: data.meetings.map((meeting) =>
      meeting.meetingId === session.meetingId ? { ...meeting, session } : meeting,
    ),
  }

type ILiveMeetingsAction = Partial<ILiveMeetingsData & IArcLiveSession> & {
  session?: IArcLiveSession
}

/**
 * Polled quietly in the background: every request shares one key, so once a list is
 * loaded a refetch keeps it on screen rather than dropping to a loading state.
 */
const liveMeetings = createRequestScopedReducer<ILiveMeetingsData, unknown, ILiveMeetingsAction>(
  [
    actions.LIST_LIVE_MEETINGS_REQUEST,
    actions.LIST_LIVE_MEETINGS_SUCCESS,
    actions.LIST_LIVE_MEETINGS_FAILURE,
  ],
  {
    keyOf: () => null,
    keepDataOnSameKey: true,
    toData: (data) => (data?.meetings ? { meetings: data.meetings } : null),
    extraCases: (state, action) => {
      switch (action.type) {
        case actions.START_ARC_LIVE_SUCCESS:
        case actions.STOP_ARC_LIVE_SUCCESS:
          return action.data?.id
            ? { ...state, data: applySession(state.data, action.data as IArcLiveSession) }
            : state
        case ARC_LIVE_SESSION_UPDATED:
          return action.data?.session
            ? { ...state, data: applySession(state.data, action.data.session) }
            : state
        default:
          return state
      }
    },
  },
)

const openSessionId = (
  state: string | null = null,
  action: IAction<{ sessionId?: string }>,
): string | null => {
  switch (action.type) {
    case actions.OPEN_ARC_LIVE_PANEL:
      return action.data?.sessionId ?? null
    case actions.CLOSE_ARC_LIVE_PANEL:
      return null
    default:
      return state
  }
}

/** Revisions reuse a chunk id, so a segment replaces its earlier version. */
const mergeSegments = (
  current: IArcLiveSegment[],
  incoming: IArcLiveSegment[],
): IArcLiveSegment[] => {
  const byChunk = new Map(current.map((segment) => [segment.chunkId, segment]))
  incoming.forEach((segment) => byChunk.set(segment.chunkId, segment))
  return [...byChunk.values()].sort((a, b) => a.startTime - b.startTime)
}

type ISessionDetailAction = Partial<IArcLiveSessionDetail> & {
  /** A stop or restart responds with the session itself. */
  id?: string
  sessionId?: string
  segments?: IArcLiveSegment[]
  decision?: IArcLiveDecision
  session?: IArcLiveSession
}

type ISessionDetailState = IRequestScopedState<IArcLiveSessionDetail>

/**
 * The listener's pushes, and the stop or restart's own response ahead of its push,
 * for the open session; anything for another session is ignored.
 */
const applyLiveEvent = (
  state: ISessionDetailState,
  action: IActionWithParams<unknown, ISessionDetailAction>,
): ISessionDetailState => {
  const detail = state.data
  const isOpenSession = (sessionId?: string) => Boolean(detail && detail.session.id === sessionId)
  const withSession = (session: IArcLiveSession): ISessionDetailState =>
    detail && isOpenSession(session.id) ? { ...state, data: { ...detail, session } } : state

  switch (action.type) {
    case ARC_LIVE_TRANSCRIPT:
      return detail && isOpenSession(action.data?.sessionId)
        ? {
            ...state,
            data: {
              ...detail,
              segments: mergeSegments(detail.segments, action.data?.segments ?? []),
            },
          }
        : state
    case ARC_LIVE_DECISION:
      return detail && action.data?.decision && isOpenSession(action.data.sessionId)
        ? { ...state, data: { ...detail, decisions: [...detail.decisions, action.data.decision] } }
        : state
    case ARC_LIVE_SESSION_UPDATED:
      return action.data?.session ? withSession(action.data.session) : state
    case actions.START_ARC_LIVE_SUCCESS:
    case actions.STOP_ARC_LIVE_SUCCESS:
      return action.data?.id ? withSession(action.data as IArcLiveSession) : state
    default:
      return state
  }
}

/**
 * The open session's transcript and triage: loaded once, then kept current by the
 * listener's pushes. Reopening the loaded session keeps it on screen.
 */
const sessionDetail = createRequestScopedReducer<
  IArcLiveSessionDetail,
  { id?: string },
  ISessionDetailAction
>(
  [
    actions.GET_ARC_LIVE_SESSION_REQUEST,
    actions.GET_ARC_LIVE_SESSION_SUCCESS,
    actions.GET_ARC_LIVE_SESSION_FAILURE,
  ],
  {
    keyOf: (params) => params?.id ?? null,
    keepDataOnSameKey: true,
    toData: (data) =>
      data?.session
        ? { session: data.session, segments: data.segments ?? [], decisions: data.decisions ?? [] }
        : null,
    extraCases: applyLiveEvent,
  },
)

export default combineReducers({
  loadingArray: loadingArrayReducer([actions.prefix]),
  liveMeetings,
  openSessionId,
  sessionDetail,
})
